import { validationMixin } from "vuelidate";
import { required, email, minValue } from "vuelidate/lib/validators";

const MINIMUMORDERAMOUNT = 0;
const MINIMUMSENDDATE = new Date();

const parseToFloat = value => parseFloat(value.toString().replace(",", ""));

const isFloat = value => !isNaN(parseToFloat(value));

const orderAmountNoLessThan = min => value => parseToFloat(value) > min;

const checkDateOrder = (Before, After, AllowEqualDates) => {
  if (!(Before instanceof Date)) return false;
  if (!(After instanceof Date)) return false;

  if (!AllowEqualDates) return Before < After;

  return Before <= After;
};

const expiryDateBeforeSendDate = (value, vm) =>
  checkDateOrder(vm.sendDate, value);

export default {
  parseToFloat,
  mixins: [validationMixin],
  validations: {
    form: {
      orderInformation: {
        firstName: {
          required
        },
        lastName: {
          required
        },
        email: {
          required,
          email
        },
        orderNumber: {
          required
        }
      },
      orderDetails: {
        amount: {
          required,
          isFloat,
          orderAmountNoLessThan: orderAmountNoLessThan(MINIMUMORDERAMOUNT)
        },
        currency: {
          required
        }
      },
      paymentRequestInformation: {
        sendDate: {
          required,
          minValue: minValue(MINIMUMSENDDATE)
        },
        expiryDate: {
          required,
          expiryDateBeforeSendDate
        },
        reason: {
          required
        }
      }
    }
  },
  computed: {
    errorMessages() {
      return {
        orderInformation: this.orderInformationErrors,
        orderDetails: this.orderDetailsErrors,
        paymentRequestInformation: this.paymentRequestInformationErrors
      };
    },
    orderInformationErrors() {
      const parent = "orderInformation";

      return {
        firstName: this.generateErrorMessage(parent, "firstName"),
        lastName: this.generateErrorMessage(parent, "lastName"),
        email: this.generateErrorMessage(parent, "email"),
        orderNumber: this.generateErrorMessage(parent, "orderNumber")
      };
    },
    orderDetailsErrors() {
      const parent = "orderDetails";

      return {
        amount: this.generateErrorMessage(parent, "amount"),
        currency: this.generateErrorMessage(parent, "currency")
      };
    },
    paymentRequestInformationErrors() {
      const parent = "paymentRequestInformation";

      return {
        sendDate: this.generateErrorMessage(parent, "sendDate"),
        expiryDate: this.generateErrorMessage(parent, "expiryDate"),
        reason: this.generateErrorMessage(parent, "reason")
      };
    },
    validationMethods() {
      return [
        this.requiredMessage,
        this.orderAmountNoLessThanMessage,
        this.isFloatMessage,
        this.emailValidationMessage,
        this.minValueDateMessage,
        this.expiryDateBeforeSendDateMessage
      ];
    }
  },
  methods: {
    generateErrorMessage(parent, name) {
      let validationInfo = this.$v.form[parent][name];
      if (!validationInfo.$dirty) return null;

      if (!validationInfo.$error) return "";

      let message = this.validationMethods.reduce(
        (errorMessage, validationMethod) =>
          errorMessage + validationMethod(validationInfo),
        ""
      );

      return message;
    },
    requiredMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("required")) return "";

      const message = "This is a required field. ";

      return validationInfo.required ? "" : message;
    },
    orderAmountNoLessThanMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("orderAmountNoLessThan")) return "";

      const message = `The order amount must be greater than ${MINIMUMORDERAMOUNT}. `;

      return validationInfo.orderAmountNoLessThan ? "" : message;
    },
    isFloatMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("isFloat")) return "";

      const message = "The amount entered is invalid. ";

      return validationInfo.isFloat ? "" : message;
    },
    emailValidationMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("email")) return "";

      const message = "The email address entered is invalid. ";

      return validationInfo.email ? "" : message;
    },
    expiryDateBeforeSendDateMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("expiryDateBeforeSendDate")) return "";

      const message =
        "The expiry date must be at least one after the send date. ";

      return validationInfo.expiryDateBeforeSendDate ? "" : message;
    },
    minValueDateMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("minValue")) return "";

      const message = "The send date cannot be a date in the past. ";

      return validationInfo.minValue ? "" : message;
    }
  }
};
